import { env } from 'next-runtime-env';

import type { BotAvailabilityPaymentOption } from '~entities/bot';
import { getBotsApi } from '~services/api/bots/dynamic';
import { api, apiTags } from '~shared/api/origin';
import { taxApi } from '~shared/api/taxApi';
import type {
	TVATRequestBodyItem,
	TVATResponseItem,
} from '~shared/api/taxApi/types';
import { VatType } from '~shared/api/taxApi/types';
import {
	eventsInbox,
	EventType,
	isMsgType,
} from '~shared/services/eventsInbox';
import type { RootState } from '~store/index';
import { replaceNetWithGrossPrices, sortShopProducts } from '~utils/payments';

import { gridApi } from '../grid';
import type {
	TProductSignature,
	TShop,
	TShopItem,
	TShopPromoCodeResponse,
	TUserBalance,
	TUserShopInfo,
} from './types';

const prefix = '/credits';

export const creditsApi = api.injectEndpoints({
	endpoints: (build) => ({
		balance: build.query<TUserBalance, { id: string }>({
			query: ({ id }) => ({
				url: `${prefix}/accounts/${id}/balance`,
			}),
			async onCacheEntryAdded(
				{},
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }
			) {
				let unsub = null;
				try {
					await cacheDataLoaded;

					unsub = eventsInbox.onMsg((msg) => {
						if (
							isMsgType(
								msg,
								EventType.BalanceChanged,
								EventType.NeuronsReceived
							)
						) {
							updateCachedData((draft) => {
								draft.balance = msg.payload.balance;
							});
						}
					});
				} catch (err) {}

				await cacheEntryRemoved;
				unsub?.();
			},
		}),
		shop: build.query<TShop, void>({
			async queryFn(_, _queryApi, _extraOptions, fetchWithBQ) {
				const {
					auth: {
						info: { id },
					},
				} = _queryApi.getState() as RootState;
				const [
					{ data: userShopData, error: userShopDataError },
					productsResponse,
				] = await Promise.all([
					fetchWithBQ(`/exo/moto/${id}/details`), // user ip and token for moto form
					fetchWithBQ(`${prefix}/mall/${id}/credits?shop=web`),
				]);
				const error = productsResponse.error || userShopDataError;
				if (error) {
					return { error };
				}
				const products = productsResponse.data as Array<TShopItem>;
				const userData: TUserShopInfo = (userShopData as TUserShopInfo) ?? {
					userIp: '',
					token: '',
					country: '',
					oneClickPaymentAvailable: false,
				};

				const vatRequestBody: TVATRequestBodyItem[] = products.map((item) => ({
					amount: item.price,
					ip: userData.userIp,
					projectId: env('NEXT_PUBLIC_MOTO_MERCHANT_ID') ?? '',
					priceType: VatType.Net,
					userId: id,
				}));
				//types are not correct below
				// @ts-ignore
				const { data: vatData, error: vatError } = await _queryApi.dispatch(
					taxApi.endpoints.productPricesWithVAT.initiate(vatRequestBody)
				);
				if (vatError) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					return { error: vatError };
				}
				replaceNetWithGrossPrices(products, vatData);

				return {
					data: sortShopProducts(products, userData),
				};
			},
			serializeQueryArgs: ({ endpointName }) => {
				return `${endpointName}`;
			},
			providesTags: [apiTags.shop],
		}),
		oneClickBuy: build.mutation<
			{
				paymentId: string;
			},
			{
				userId: string;
				sku: string;
				PromoCode?: string;
				country: string;
				shownInfo: {
					price: number;
					currency: string;
				};
			}
		>({
			query: ({ userId, ...body }) => ({
				url: `/exo/moto/${userId}/payments/oneclick`,
				method: 'POST',
				body,
			}),
		}),
		applyPromoCode: build.mutation<
			TShopPromoCodeResponse,
			{ PromoCode: string }
		>({
			// TODO: force Backend team to move tax calc from client finally
			async queryFn({ PromoCode }, _queryApi, _extraOptions, fetchWithBQ) {
				const {
					auth: {
						info: { id },
					},
					api: {
						queries: { shop },
					},
				} = _queryApi.getState() as RootState;

				const resp = await fetchWithBQ({
					url: `/ai/benefits/${id}/promocode/validate`,
					method: 'POST',
					body: {
						PromoCode,
					},
				});

				const error = resp.error;
				if (error) {
					return { error };
				}
				const shopData = shop?.data as TShop;
				const data = resp.data as TShopPromoCodeResponse;
				const vatRequestBody: TVATRequestBodyItem[] = data.Prices.map(
					(item) => ({
						amount: item.PromoPrice,
						ip: shopData.userIp,
						projectId: env('NEXT_PUBLIC_MOTO_MERCHANT_ID') ?? '',
						priceType: VatType.Net,
						userId: id,
					})
				);
				//types are not correct below
				// @ts-ignore
				const { data: vatData, error: vatError } = await _queryApi.dispatch(
					taxApi.endpoints.productPricesWithVAT.initiate(vatRequestBody)
				);
				if (vatError) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					return { error: vatError };
				}
				const priceMap: Map<number, number> = new Map(
					(vatData as TVATResponseItem[]).map((price) => [
						price.net,
						price.gross,
					])
				);

				data.Prices.forEach((item) => {
					const newPrice = priceMap.get(item.PromoPrice);
					if (newPrice !== undefined) {
						item.PromoPrice = newPrice;
					}
				});
				return {
					data: { ...(resp.data as TShopPromoCodeResponse), PromoCode },
				};
			},
		}),
		buyBot: build.mutation<
			undefined,
			{
				id: string;
				botId: string | number;
				type: BotAvailabilityPaymentOption['type'];
			}
		>({
			query: ({ id, botId, type }) => ({
				url: `/ai/chats/userId/${id}/botId/${botId}/buy`,
				method: 'POST',
				body: {
					type,
				},
			}),
			// TODO: Maybe invalidesTags ... ?
			async onQueryStarted(
				{ botId, id },
				{ dispatch, queryFulfilled, getState }
			) {
				const botsApi = await getBotsApi();
				try {
					await queryFulfilled;

					dispatch(
						botsApi.util.updateQueryData(
							'getContacts',
							{ userId: id },
							(draft) => {
								draft.contacts[botId].preConditions = undefined;
							}
						)
					);
					for (const { originalArgs } of gridApi.util.selectInvalidatedBy(
						getState(),
						[apiTags.grid]
					)) {
						dispatch(
							gridApi.util.updateQueryData('getGrid', originalArgs, (draft) => {
								const bot = draft.botGrid.find(
									({ clientId }) => clientId === +botId
								);
								if (bot) {
									bot.preCondition = undefined;
								}
							})
						);
					}
				} catch {}
			},
		}),
		paymentSignature: build.query<
			TProductSignature,
			{ userId: string; sku: string; promoCode?: string }
		>({
			query: ({ userId, sku, promoCode }) => ({
				url: `/exo/moto/${userId}/payments`,
				method: 'POST',
				body: {
					sku,
					promoCode,
				},
			}),
		}),
	}),
});

export const {
	useBalanceQuery,
	useShopQuery,
	useBuyBotMutation,
	useOneClickBuyMutation,
	useApplyPromoCodeMutation,
	usePaymentSignatureQuery,
} = creditsApi;
